import { DndContext, useDraggable, useDroppable, DragOverlay } from "@dnd-kit/core";
import { useState } from "react";
import BoardColumn from "./BoardColumn";
import IssueCard from "./IssueCard";
import { KANBAN_COLUMN_WIDTH_PX } from "./kanbanBoardLayout";
import Typography from "@/Utility/Typography";
import { usePermissions } from "@/hooks/usePermissions";
import { PERMISSION_KEYS } from "@/permissions/permissionKeys";
import PermissionDeniedModal from "@/Utility/PermissionDeniedModal/PermissionDeniedModal";

const STATUSES = [
  { key: "open", label: "To Do", status: "Open" },
  { key: "in progress", label: "In Progress", status: "In Progress" },
  { key: "blocked", label: "Blocked", status: "Blocked" },
  { key: "done", label: "Done", status: "Done" },
];

const ScrumBoard = ({ issues, onStatusChange, projectSlug, onOpenWorkItemDetail }) => {
  const { can } = usePermissions();
  const canEditWorkItem = can(PERMISSION_KEYS.WORK_ITEMS_EDIT);
  const [activeId, setActiveId] = useState(null);
  const [deniedPermissionKey, setDeniedPermissionKey] = useState(null);
  // Show issues with sprintId in sprint columns
  // Backlog shows items without sprintId
  const activeSprint = issues.filter((i) => i.sprintId === 1);
  const backlog = issues.filter(
    (i) => !i.sprintId || i.sprintId === undefined || i.sprintId === null
  );
  const activeIssue = activeId ? issues.find((i) => i.id === activeId) : null;

  return (
    <>
      <DndContext
        onDragStart={({ active }) => {
          setActiveId(active.id);
        }}
        onDragEnd={({ active, over }) => {
          setActiveId(null);
          if (over && active.id !== over.id) {
            if (!canEditWorkItem) {
              setDeniedPermissionKey(PERMISSION_KEYS.WORK_ITEMS_EDIT);
              return;
            }
            // Find the status that matches the drop target
            const targetStatus = STATUSES.find((s) => s.key === over.id);
            if (targetStatus) {
              // When dragging to a sprint column, assign to sprint 1
              // Check if the dragged issue is from backlog (no sprintId)
              const draggedIssue = issues.find((i) => i.id === active.id);
              const shouldAssignSprint = draggedIssue && !draggedIssue.sprintId;
              onStatusChange(active.id, targetStatus.status, shouldAssignSprint ? 1 : undefined);
            }
          }
        }}
        onDragCancel={() => {
          setActiveId(null);
        }}
      >
        <div className="space-y-8">
          {/* Sprint Header */}
          <div className="bg-white border rounded-lg p-4">
            <div className="flex justify-between items-center">
              <div>
                <Typography variant="h3" component="h3" className="font-semibold text-lg">
                  Sprint 12
                </Typography>
                <Typography className="text-sm text-gray-500" component="p">
                  04/10 – 04/24
                </Typography>
              </div>

              <div className="text-sm font-medium text-green-600">55% Completed</div>
            </div>
            <Typography className="mt-3 text-sm" component="p">
              <Typography variant="span2" className="font-semibold">
                Sprint Goal:
              </Typography>{" "}
              User authentication & API hardening
            </Typography>

            <div className="mt-3 h-2 bg-gray-200 rounded">
              <div className="h-2 bg-green-500 rounded w-[55%]" />
            </div>
          </div>

          {/* Active Sprint */}
          <div>
            <Typography variant="h4" className="font-semibold mb-4">
              Active Sprint
            </Typography>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {STATUSES.map((status) => (
                <SprintColumn
                  key={status.key}
                  status={status}
                  issues={activeSprint.filter((i) => i.status?.toLowerCase() === status.key)}
                  projectSlug={projectSlug}
                  canEditWorkItem={canEditWorkItem}
                  onOpenWorkItemDetail={onOpenWorkItemDetail}
                />
              ))}
            </div>
          </div>

          {/* Product Backlog */}
          <div>
            <Typography variant="h4" className="font-semibold mb-4">
              Product Backlog
            </Typography>

            <div className="space-y-3">
              {backlog.length > 0 ? (
                backlog.map((issue) => (
                  <DraggableIssue
                    key={issue.id}
                    issue={issue}
                    projectSlug={projectSlug}
                    canEditWorkItem={canEditWorkItem}
                    onOpenWorkItemDetail={onOpenWorkItemDetail}
                  />
                ))
              ) : (
                <Typography className="text-sm text-gray-500" component="p">
                  No backlog items
                </Typography>
              )}
            </div>
          </div>
        </div>
        <DragOverlay>
          {activeIssue ? (
            <div
              className="rotate-3 opacity-90 min-w-0 shrink-0"
              style={{
                width: `${KANBAN_COLUMN_WIDTH_PX}px`,
                minWidth: `${KANBAN_COLUMN_WIDTH_PX}px`,
              }}
            >
              <IssueCard
                issue={activeIssue}
                projectSlug={projectSlug}
                onOpenWorkItemDetail={onOpenWorkItemDetail}
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      <PermissionDeniedModal
        isOpen={deniedPermissionKey != null}
        onClose={() => setDeniedPermissionKey(null)}
        permissionKey={deniedPermissionKey}
      />
    </>
  );
};

const SprintColumn = ({ status, issues, projectSlug, canEditWorkItem, onOpenWorkItemDetail }) => {
  const { setNodeRef, isOver } = useDroppable({ id: status.key });

  return (
    <div ref={setNodeRef} className={isOver ? "ring-2 ring-blue-500 ring-offset-2 rounded-lg" : ""}>
      <BoardColumn title={status.label}>
        {issues.map((issue) => (
          <DraggableIssue
            key={issue.id}
            issue={issue}
            projectSlug={projectSlug}
            canEditWorkItem={canEditWorkItem}
            onOpenWorkItemDetail={onOpenWorkItemDetail}
          />
        ))}
      </BoardColumn>
    </div>
  );
};

const DraggableIssue = ({ issue, projectSlug, canEditWorkItem = true, onOpenWorkItemDetail }) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: issue.id,
    disabled: !canEditWorkItem,
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      className="w-full min-w-0 max-w-full"
      style={style}
      {...(canEditWorkItem ? listeners : {})}
      {...(canEditWorkItem ? attributes : {})}
    >
      <IssueCard
        issue={issue}
        projectSlug={projectSlug}
        dragDisabled={!canEditWorkItem}
        onOpenWorkItemDetail={onOpenWorkItemDetail}
      />
    </div>
  );
};

export default ScrumBoard;
